import React, { memo, useCallback, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Label,
} from "recharts";
import _ from "lodash";
import styles from "./style.module.scss";
import { CHART_COLORS, getRandomHexColor } from "../../../../../services/colorUtil";
import { formatAxisTick, formatChartNumber } from "../chartNumberFormat";

interface SeriesData {
  label: string;
  x: (string | number)[];
  y: number[];
}

interface ChartArtifacts {
  meta: {
    xlabel: string;
    ylabel: string;
    title?: string;
  };
  series: SeriesData[];
}

interface BarChartViewProps {
  artifacts: ChartArtifacts;
}

function barItemSorter(item: { value?: unknown }) {
  return -(Number(item.value) || 0);
}

const chartStyles = {
  labelStyle: { color: "#374151", fontWeight: 600 },
  contentStyle: {
    backgroundColor: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    fontWeight: 500,
    fontSize: "14px",
  },
};

const BarChartView = memo<BarChartViewProps>(function BarChartView({ artifacts }) {
  const [activeBar, setActiveBar] = useState<string | null>(null);

  const prepared = useMemo(() => {
    const seriesArray = _.get(artifacts, "series", []) as SeriesData[];
    if (_.isEmpty(seriesArray)) return null;

    const meta = _.get(artifacts, "meta", {}) as ChartArtifacts["meta"];
    const { xlabel, ylabel } = meta;
    const xValues = seriesArray[0].x;

    const data = xValues.map((x, index) => {
      const point: Record<string, number | string> = { x };
      seriesArray.forEach((series) => {
        point[series.label] = series.y[index];
      });
      return point;
    });

    return { xlabel, ylabel, seriesArray, data };
  }, [artifacts]);

  const handleLegendClick = useCallback((label: string) => {
    setActiveBar((prev) => (prev === label ? null : label));
  }, []);

  const tooltipFormatter = useCallback(
    (value: number, name: string) => {
      if (activeBar === null) {
        return [formatChartNumber(Number(value), "fixed2"), name];
      }
      if (activeBar === name) {
        return [formatChartNumber(Number(value), "fixed2"), name];
      }
      return null;
    },
    [activeBar]
  );

  const legendContent = useCallback(
    (props: any) => (
      <BarLegendView
        {...props}
        activeBar={activeBar}
        onLegendClick={handleLegendClick}
      />
    ),
    [activeBar, handleLegendClick]
  );

  if (!prepared) return null;

  const { xlabel, ylabel, seriesArray, data } = prepared;

  const calculateOpacity = (label: string) => {
    if (activeBar === null) return 1;
    return activeBar === label ? 1 : 0.15;
  };

  return (
    <div className={styles.chartContainer}>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={data}
          margin={{
            top: 10,
            right: 30,
            left: 30,
            bottom: 50,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="x">
            <Label
              value={xlabel}
              offset={-15}
              position="insideBottom"
              className={styles.label}
            />
          </XAxis>
          <YAxis tickFormatter={formatAxisTick}>
            <Label
              value={ylabel}
              angle={-90}
              position="left"
              offset={8}
              className={styles.label}
            />
          </YAxis>

          <Tooltip
            contentStyle={chartStyles.contentStyle}
            labelStyle={chartStyles.labelStyle}
            allowEscapeViewBox={{ x: false, y: false }}
            formatter={tooltipFormatter}
            itemSorter={barItemSorter}
            wrapperStyle={{ pointerEvents: "auto" }}
          />

          <Legend verticalAlign="top" content={legendContent} />

          {seriesArray.map((series, index) => (
            <Bar
              key={series.label}
              dataKey={series.label}
              name={series.label}
              fill={
                index < CHART_COLORS.length
                  ? CHART_COLORS[index]
                  : getRandomHexColor()
              }
              barSize={25}
              opacity={calculateOpacity(series.label)}
              isAnimationActive={false}
            />
          ))}
          <text x="100%" y="95%" textAnchor="end" dominantBaseline="auto" className={styles.disclaimer}>* AI Generated Graph</text>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
});

const BarLegendView = memo<{
  payload: any[];
  activeBar: string | null;
  onLegendClick: (label: string) => void;
}>(function BarLegendView({
  payload,
  activeBar,
  onLegendClick,
}) {
  return (
    <ul className={styles.legendContainer}>
      {payload.map((entry: any, index: number) => {
        const isActive = activeBar === null || activeBar === entry.value;

        return (
          <li
            key={`legend-${index}`}
            onClick={() => onLegendClick(entry.value)}
            className={styles.legendList}
            style={{
              color: isActive ? entry.color : "#cbd5e1",
              cursor: "pointer",
            }}
          >
            <span
              className={styles.legendName}
              style={{
                backgroundColor: isActive ? entry.color : "#cbd5e1",
              }}
            />
            {entry.value}
          </li>
        );
      })}
    </ul>
  );
});

export default BarChartView;
